import React, { useState, useRef, useEffect } from "react";
import apiService from "../../services/api";

interface MessageInputProps {
  onSend: (text: string, mediaUrl?: string) => void;
  onTemplateClick: () => void;
  disabled?: boolean;
  canSendFreeform: boolean;
}

function isImageFile(file: File) {
  return file.type.startsWith("image/");
}

export const MessageInput: React.FC<MessageInputProps> = ({
  onSend,
  onTemplateClick,
  disabled,
  canSendFreeform,
}) => {
  const [text, setText] = useState("");
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const token = localStorage.getItem("token") || "";

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 96) + "px";
  }, [text]);

  // Build/revoke object URL for image preview
  useEffect(() => {
    if (!pendingFile || !isImageFile(pendingFile)) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(pendingFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingFile]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = async () => {
    if (disabled || !canSendFreeform) return;
    if (pendingFile) {
      await sendFile(pendingFile);
      return;
    }
    const trimmed = text.trim();
    if (!trimmed) return;
    onSend(trimmed);
    setText("");
  };

  const sendFile = async (file: File) => {
    setIsUploading(true);
    try {
      const res = await apiService.uploadWhatsAppMedia(token, file);
      onSend(text.trim(), res.url);
      setText("");
      setPendingFile(null);
    } catch (err) {
      console.error("[MessageInput] Upload failed", err);
      alert("שגיאה בהעלאת הקובץ");
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setPendingFile(file);
  };

  const cancelPending = () => {
    setPendingFile(null);
    setPreviewUrl(null);
  };

  const isBusy = disabled || isUploading;
  const canSend = !isBusy && (!!pendingFile || !!text.trim());

  if (!canSendFreeform) {
    return (
      <div
        style={{
          borderTop: "1px solid #ddd",
          backgroundColor: "#f0f0f0",
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <div style={{ flex: 1, color: "#888", fontSize: 13 }}>
          יותר מ-24 שעות מאז ההודעה האחרונה. ניתן לשלוח רק הודעות תבנית מאושרות.
        </div>
        <button
          onClick={onTemplateClick}
          style={{
            backgroundColor: "#3A7380",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            padding: "8px 16px",
            fontSize: 13,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          בחר תבנית
        </button>
      </div>
    );
  }

  return (
    <div style={{ borderTop: "1px solid #ddd", backgroundColor: "#f0f0f0" }}>
      {/* File preview bar */}
      {pendingFile && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "8px 12px",
            backgroundColor: "#fff",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          {previewUrl ? (
            <img
              src={previewUrl}
              alt="preview"
              style={{ height: 64, width: 64, objectFit: "cover", borderRadius: 6, flexShrink: 0 }}
            />
          ) : (
            <div
              style={{
                height: 64,
                width: 64,
                borderRadius: 6,
                backgroundColor: "#f0f0f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 28,
                flexShrink: 0,
              }}
            >
              📄
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {pendingFile.name}
            </div>
            <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
              {(pendingFile.size / 1024).toFixed(0)} KB
            </div>
            {isUploading && (
              <div style={{ fontSize: 11, color: "#3A7380", marginTop: 2 }}>מעלה...</div>
            )}
          </div>
          <button
            onClick={cancelPending}
            disabled={isUploading}
            style={{
              background: "none",
              border: "none",
              fontSize: 18,
              color: "#aaa",
              cursor: isUploading ? "not-allowed" : "pointer",
              padding: 4,
              flexShrink: 0,
            }}
            title="בטל"
          >
            ✕
          </button>
        </div>
      )}

      {/* Input row */}
      <div style={{ padding: "8px 12px", display: "flex", alignItems: "flex-end", gap: 8 }}>
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        {/* Template button */}
        <button
          onClick={onTemplateClick}
          title="תבניות הודעה"
          disabled={isBusy}
          style={{
            background: "none",
            border: "none",
            cursor: isBusy ? "not-allowed" : "pointer",
            fontSize: 20,
            color: "#555",
            padding: "4px",
            flexShrink: 0,
            alignSelf: "flex-end",
            marginBottom: 4,
          }}
        >
          📋
        </button>

        {/* Attach button */}
        <button
          onClick={() => fileInputRef.current?.click()}
          title="שלח קובץ או תמונה"
          disabled={isBusy}
          style={{
            background: "none",
            border: "none",
            cursor: isBusy ? "not-allowed" : "pointer",
            fontSize: 20,
            color: "#555",
            padding: "4px",
            flexShrink: 0,
            alignSelf: "flex-end",
            marginBottom: 4,
          }}
        >
          📎
        </button>

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={pendingFile ? "הוסף כיתוב (אופציונלי)..." : "הקלד הודעה..."}
          disabled={isBusy}
          rows={1}
          style={{
            flex: 1,
            resize: "none",
            border: "none",
            borderRadius: 20,
            padding: "10px 14px",
            fontSize: 14,
            lineHeight: 1.4,
            outline: "none",
            backgroundColor: "#fff",
            direction: "rtl",
            overflowY: "auto",
            maxHeight: 96,
            fontFamily: "inherit",
          }}
        />

        {/* Send button */}
        <button
          onClick={handleSend}
          disabled={!canSend}
          style={{
            backgroundColor: canSend ? "#3A7380" : "#ccc",
            border: "none",
            borderRadius: "50%",
            width: 40,
            height: 40,
            cursor: canSend ? "pointer" : "not-allowed",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            fontSize: 16,
            color: "#fff",
            transition: "background-color 0.2s",
          }}
        >
          ▶
        </button>
      </div>
    </div>
  );
};
